import type { DataGridFilterOption } from '@ibsys/kit';
import { isNotEmptyFilterValue } from '@/shared/lib';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { formatSecondsToTime } from '@/shared/lib/utils/string';
import { formatNumericFilterItem } from '@/shared/ui';
import type { ColumnDescriptor } from './columnDescriptors';
import { type FilterRange, isEmptyBound, isEmptyFilterValue, isFilterRange } from './filterValue';
import type { DescribeContext, DescriptionFilterEditor, DescriptionPart, DescriptionValue } from './types';

/**
 * Подпись выбранного значения из списка опций фильтра. Список необязателен: у фильтра с опциями
 * из бэкенда заранее известных значений нет — значение называет само себя, пока подпись не найдена.
 */
const getOptionLabel = (options: DataGridFilterOption[] | undefined, value: unknown): string =>
  options?.find((option) => String(option.value) === String(value))?.label ?? String(value);

/** Календарь фильтра отдаёт дату объектом, а сохранённое состояние таблицы — строкой */
const parseDate = (value: unknown): Date | null => {
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const describeFilter = (
  descriptor: ColumnDescriptor,
  value: unknown,
  ctx: DescribeContext,
): DescriptionPart | null => {
  if (isEmptyFilterValue(value)) return null;

  const { t } = ctx;

  // Фильтр открывается из описания тем же полем, что и из шапки колонки: встроенный — по своей
  // конфигурации, кастомный — своим рендерером.
  const editor: DescriptionFilterEditor | undefined =
    descriptor.filter || descriptor.renderFilter
      ? { value, config: descriptor.filter, render: descriptor.renderFilter }
      : undefined;

  const buildPart = (connector: string | null, values: DescriptionValue[], isFoldable = false): DescriptionPart => ({
    id: descriptor.id,
    kind: 'filter',
    label: descriptor.header,
    connector,
    values,
    isFoldable,
    editor,
  });

  const describeEquals = (text: string): DescriptionPart =>
    buildPart(t('dialogs:dialogListSection.filtersDescription.connector.equals'), [{ text }]);

  /** Значение читается за той же связкой, что и у выбора: слов о границах диапазона в нём нет */
  const describeValue = (text: string | null): DescriptionPart | null => (text ? describeEquals(text) : null);

  /** Несколько выбранных значений перечисляются за той же связкой, что и одно */
  const describeSeveral = (labels: string[]): DescriptionPart =>
    buildPart(
      t('dialogs:dialogListSection.filtersDescription.connector.equals'),
      labels.map((text) => ({ text })),
      true,
    );

  /** Диапазон читается парой границ через дефис; незаданная граница оставляет дефис со своей стороны */
  const describeRange = (range: FilterRange, formatBound: (bound: unknown) => string | null): string | null => {
    const from = isEmptyBound(range.from) ? null : formatBound(range.from);
    const to = isEmptyBound(range.to) ? null : formatBound(range.to);
    if (from && to) return t('dialogs:dialogListSection.filtersDescription.range', { from, to });
    if (from) return t('dialogs:dialogListSection.filtersDescription.rangeFrom', { from });
    if (to) return t('dialogs:dialogListSection.filtersDescription.rangeTo', { to });
    return null;
  };

  const describeDateRange = (range: FilterRange, withTime: boolean): string | null =>
    describeRange(range, (bound) => {
      const date = parseDate(bound);
      return date ? formatHumanDate(ctx.i18n, date, { withTime }) : null;
    });

  // Отбор по заполненности значения не имеет: он называется тем же, чем назван в самом отборе.
  if (isNotEmptyFilterValue(value)) {
    return buildPart(null, [{ text: t('common:dataGrid.filterNotEmpty') }]);
  }

  // Фильтр участников хранит только идентификаторы: имена запрашиваются по ним самим отбором,
  // поэтому количество выбранных стоит в описании лишь пока имена не получены.
  if (descriptor.meta?.personRole) {
    const ids = (Array.isArray(value) ? value : [value]).map(Number);
    if (ctx.isPersonNamesLoading) {
      return describeEquals(t('dialogs:dialogListSection.filtersDescription.selectedCount', { count: ids.length }));
    }
    // Участника, которого справочник не знает (например, удалённого), называет его идентификатор:
    // отбор по нему действует, и умолчать о нём нельзя.
    return describeSeveral(
      ids.map((id) => ctx.personNames[id] ?? t('dialogs:dialogListSection.filtersDescription.unknownPerson', { id })),
    );
  }

  // Числовой отбор задан либо границами, либо перечнем набранных элементов: конфигурации фильтра
  // у такой колонки нет, поэтому режим виден только по самому значению.
  if (descriptor.meta?.hasNumericFilter) {
    // Элемент перечня называется так же, как показан в самом отборе: значением либо парой границ
    if (Array.isArray(value)) return describeSeveral(value.map(formatNumericFilterItem));
    return isFilterRange(value) ? describeValue(describeRange(value, (bound) => String(bound))) : null;
  }

  const config = descriptor.filter;
  if (!config) return null;

  switch (config.type) {
    case 'text':
      return buildPart(t('dialogs:dialogListSection.filtersDescription.connector.contains'), [
        {
          text: t('dialogs:dialogListSection.filtersDescription.quoted', { value: String(value) }),
        },
      ]);
    case 'boolean':
      return describeEquals(
        value === true ? (config.labelTrue ?? t('common:yes')) : (config.labelFalse ?? t('common:no')),
      );
    case 'select':
      return describeEquals(getOptionLabel(config.options, value));
    // Кит отдаёт набор массивом, но в сохранённых настройках таблицы могло остаться одиночное
    // значение прежнего режима выбора — читается и оно.
    case 'multiselect':
      return Array.isArray(value)
        ? describeSeveral(value.map((item) => getOptionLabel(config.options, item)))
        : describeEquals(getOptionLabel(config.options, value));
    case 'integer':
      return isFilterRange(value) ? describeValue(describeRange(value, (bound) => String(bound))) : null;
    // Действующий формат отбор уже несёт в себе: полем времени вводится только то, что и
    // показано как время, — значит и граница называется как время. В формате «Секунды» отбор
    // целочисленный, его граница читается числом выше, а единицу измерения даёт подпись колонки.
    case 'time-range':
      return isFilterRange(value)
        ? describeValue(
            describeRange(value, (bound) =>
              config.returnType === 'seconds' && typeof bound === 'number' ? formatSecondsToTime(bound) : String(bound),
            ),
          )
        : null;
    case 'date':
    case 'timestamp': {
      const date = parseDate(value);
      return date ? describeEquals(formatHumanDate(ctx.i18n, date, { withTime: config.type === 'timestamp' })) : null;
    }
    case 'date-range':
      return isFilterRange(value) ? describeValue(describeDateRange(value, false)) : null;
    case 'timestamp-range':
      return isFilterRange(value) ? describeValue(describeDateRange(value, true)) : null;
  }
};
